import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { GraduationCap, Users, Heart, BookOpen } from "lucide-react";
import ejlogo from "../ejlogo.svg";

interface LandingProps {
  onSelectMentor: () => void;
  onSelectScholar: () => void;
}

export function Landing({ onSelectMentor, onSelectScholar }: LandingProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <header className="container mx-auto px-4 py-16 text-center">
        <div className="flex justify-center mb-6">
          <a href="https://educationaljustice.org" target="_blank" rel="noopener noreferrer">
            <img src={ejlogo} alt="Educational Justice" className="h-20" />
          </a>
        </div>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-6">
          Empowering students through mentorship. Connecting high school mentors 
          with middle school scholars for a brighter future.
        </p>
        <a
          href="https://educationaljustice.org/wp-content/uploads/2025/09/1000-Family-Scholarship-2025-26.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200/60 text-sm font-medium text-emerald-800 hover:from-emerald-100 hover:to-amber-100 transition-all animate-pulse-subtle group"
        >
          <span className="text-lg">💵</span>
          <span>Scholars can win a $1,000 Scholarship!</span>
          <span className="text-emerald-600 group-hover:translate-x-0.5 transition-transform">→</span>
        </a>
      </header>

      {/* Sign Up Cards */}
      <section className="container mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Mentor Card */}
          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors hover:shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-2xl text-blue-900">Become a Mentor</CardTitle>
              <CardDescription className="text-base">
                For high school students (9th-12th grade)
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-slate-600 mb-6">
                Share your knowledge and experience to guide younger students. 
                Make a lasting impact while developing leadership skills.
              </p>
              <ul className="text-left text-sm text-slate-500 mb-6 space-y-2">
                <li className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-blue-500" />
                  Guide and inspire younger students
                </li>
                <li className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  Build leadership experience
                </li>
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  Join a community of changemakers
                </li>
              </ul>
              <Button 
                onClick={onSelectMentor} 
                className="w-full bg-blue-600 hover:bg-blue-700" 
                size="lg"
              >
                Sign Up as Mentor
              </Button>
            </CardContent>
          </Card>

          {/* Scholar Card */}
          <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors hover:shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <GraduationCap className="h-8 w-8 text-emerald-600" />
                </div>
              </div>
              <CardTitle className="text-2xl text-emerald-900">Become a Scholar</CardTitle>
              <CardDescription className="text-base">
                For middle school students (5th-8th grade)
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-slate-600 mb-6">
                Get matched with a high school mentor who will support your 
                academic journey and help you reach your goals.
              </p>
              <ul className="text-left text-sm text-slate-500 mb-6 space-y-2">
                <li className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-emerald-500" />
                  Receive personalized guidance
                </li>
                <li className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-emerald-500" />
                  Improve academic performance
                </li>
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-emerald-500" />
                  Connect with a supportive mentor
                </li>
              </ul>
              <Button 
                onClick={onSelectScholar} 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                size="lg"
              >
                Sign Up as Scholar
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-slate-50">
        <div className="container mx-auto px-4 py-8 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} Educational Justice. All rights reserved.</p>
          <p className="mt-2">Empowering the next generation through mentorship.</p>
        </div>
      </footer>
    </div>
  );
}
